import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  Row,
  Col,
  Statistic,
  Tabs,
  Tag,
  Button,
  Input,
  Select,
  Checkbox,
  Space,
  Progress,
  Card,
  Table
} from 'antd'
import {
  SearchOutlined,
  TruckOutlined,
  UserOutlined,
  PlusOutlined,
  FilterOutlined,
  CloseOutlined,
  DownOutlined,
  ReloadOutlined,
  MoreOutlined,
  ScanOutlined,
  InboxOutlined,
  MenuOutlined,
  PrinterOutlined
} from '@ant-design/icons'
import './OnboardPreviewPhone.css'

const formatDecimal = (value) => Number(value || 0).toFixed(2)

const formatHoraLimite = (value) => {
  if (value === null || value === undefined || value === '') return ''

  if (typeof value === 'number') {
    const raw = String(Math.trunc(value)).padStart(4, '0')
    return `${raw.slice(0, 2)}:${raw.slice(2, 4)}`
  }

  const raw = String(value).trim()
  if (!raw) return ''

  if (raw.includes(':')) {
    const [hh = '', mm = ''] = raw.split(':')
    return `${hh.padStart(2, '0').slice(0, 2)}:${mm.padStart(2, '0').slice(0, 2)}`
  }

  const digits = raw.replace(/\D/g, '')
  if (digits.length >= 4) {
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`
  }

  return ''
}

const calcClientTotals = (notas = []) => {
  const impressas = notas.filter((nota) => Number(nota.status) === 1)
  const naoImpressas = notas.filter((nota) => Number(nota.status) === 0)

  const buildTotals = (rows) => ({
    notas: rows.length,
    vols: rows.reduce((sum, row) => sum + Number(row.volumes || 0), 0),
    peso: rows.reduce((sum, row) => sum + Number(row.peso || 0), 0),
    m3: rows.reduce((sum, row) => sum + Number(row.m3 || 0), 0)
  })

  return {
    impressas: buildTotals(impressas),
    naoImpressas: buildTotals(naoImpressas)
  }
}

const getProgressColor = (percent) => {
  if (percent <= 50) return '#52c41a'
  if (percent <= 90) return '#faad14'
  return '#ff4d4f'
}

const renderResumoNotas = (title, danger, totals) => {
  if (!totals || totals.notas <= 0) return null

  return (
    <div className={`onboard-preview-summary-table ${danger ? 'danger' : 'success'}`}>
      <div className="onboard-preview-summary-title">{title}</div>
      <table>
        <thead>
          <tr>
            <th>NOTAS</th>
            <th>VOLS</th>
            <th>PESO</th>
            <th>M3</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{totals.notas}</td>
            <td>{totals.vols}</td>
            <td>{formatDecimal(totals.peso)}</td>
            <td>{formatDecimal(totals.m3)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const renderEmptyState = (title, subtitle) => (
  <div className="onboard-preview-empty-state">
    <InboxOutlined className="onboard-preview-empty-icon" />
    <div className="onboard-preview-empty-title">{title}</div>
    {subtitle ? <div className="onboard-preview-empty-subtitle">{subtitle}</div> : null}
  </div>
)

const renderColetas = (coletas = [], onSelectColeta, selectedColeta) => {
  if (!coletas.length) {
    return renderEmptyState('Nenhuma coleta encontrada', 'Este preview exibe apenas os dados disponíveis para o veículo selecionado.')
  }

  return (
    <div className="onboard-preview-list">
      {coletas.map((coleta) => (
        <div
          key={`${coleta.CdInscricao}-${coleta.NrColeta}`}
          className={`onboard-preview-coleta-section ${selectedColeta?.CdInscricao === coleta.CdInscricao && selectedColeta?.NrColeta === coleta.NrColeta ? 'is-selected' : ''}`}
        >
          <div
            className="onboard-preview-coleta-header"
            onClick={() => onSelectColeta(coleta)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelectColeta(coleta)
              }
            }}
          >
            <span>{coleta.NrColeta}</span>
            <strong title={coleta.DsEntidade}>{coleta.DsEntidade}</strong>
          </div>

          {(formatHoraLimite(coleta.hrlimite) || coleta.DsObservacao) ? (
            <div className="onboard-preview-coleta-meta">
              <span>{formatHoraLimite(coleta.hrlimite) ? `Hora limite: ${formatHoraLimite(coleta.hrlimite)}` : ''}</span>
              <span>{coleta.DsObservacao ? `Obs.: ${String(coleta.DsObservacao).slice(0, 42)}` : ''}</span>
            </div>
          ) : null}

          {renderResumoNotas('NOTAS IMPRESSAS', false, calcClientTotals(coleta.notas).impressas)}
          {renderResumoNotas('NÃO IMPRESSAS', true, calcClientTotals(coleta.notas).naoImpressas)}
        </div>
      ))}
    </div>
  )
}

const renderNotas = (coleta, expandedNotas, onToggleNota) => {
  if (!coleta?.notas?.length) {
    return renderEmptyState('Nenhuma nota encontrada', 'Selecione uma coleta na aba anterior para visualizar as notas do cliente.')
  }

  return (
    <div className="onboard-preview-notas">
      <Row gutter={16} className="onboard-preview-selected-client-row">
        <Col span={21}>
          <div className="onboard-preview-client-name" title={coleta.DsEntidade}>
            {coleta.NrColeta ? `${coleta.NrColeta} - ` : ''}{coleta.DsEntidade}
          </div>
        </Col>
        <Col span={3} className="onboard-preview-plus-col">
          <Button type="primary" shape="circle" icon={<PlusOutlined />} size="large" disabled />
        </Col>
      </Row>

      <div className="onboard-preview-scroll-container">
        <div className="onboard-preview-toolbar">
          <Select value="Nota" disabled style={{ width: '38%' }}>
            <Select.Option value="Nota">Nota</Select.Option>
            <Select.Option value="Pedido">Pedido</Select.Option>
            <Select.Option value="Praça">Praça</Select.Option>
            <Select.Option value="CD">CD</Select.Option>
            <Select.Option value="UF">UF</Select.Option>
            <Select.Option value="Status">Não impressas</Select.Option>
          </Select>
          <Space.Compact style={{ width: '62%' }}>
            <Input disabled placeholder="Número da nota" />
            <Button disabled icon={<SearchOutlined />} />
            <Button disabled icon={<FilterOutlined />} />
            <Button disabled icon={<CloseOutlined />} />
          </Space.Compact>
        </div>

        <div className="onboard-preview-actions">
          <Checkbox disabled>Selecionar notas não impressas</Checkbox>
          <Button disabled type="primary" icon={<PrinterOutlined />} />
        </div>

        <div className="onboard-preview-list">
          {coleta.notas.map((nota, index) => {
            const notaKey = `${nota.cdremetente}-${nota.nota}-${nota.serie}-${index}`
            const expanded = expandedNotas.includes(notaKey)

            return (
              <div key={notaKey} className={`onboard-preview-note-card ${expanded ? 'expanded' : ''}`}>
                <div className="onboard-preview-note-header">
                  <div className="onboard-preview-note-header-left">
                    <Checkbox checked={false} disabled />
                    <strong>NF: {nota.nota}</strong>
                  </div>
                  <div className="onboard-preview-note-header-right">
                    <span>PED: {nota.pedido || '0'}</span>
                    {Number(nota.status) === 0 ? (
                      <Button
                        type="text"
                        size="small"
                        disabled
                        icon={<PrinterOutlined style={{ color: '#ff4d4f' }} />}
                      />
                    ) : null}
                    <DownOutlined className="onboard-preview-down-icon" onClick={() => onToggleNota(notaKey)} />
                  </div>
                </div>

                {expanded ? (
                  <div className="onboard-preview-note-expanded">
                    <div className="onboard-preview-note-grid">
                      <div>
                        <span>VOL(S)</span>
                        <strong>{nota.volumes}</strong>
                      </div>
                      <div>
                        <span>PESO</span>
                        <strong>{formatDecimal(nota.peso)}</strong>
                      </div>
                      <div>
                        <span>M3</span>
                        <strong>{formatDecimal(nota.m3)}</strong>
                      </div>
                    </div>

                    <div className="onboard-preview-note-destination">
                      <span>Destinatário</span>
                      <strong>{nota.destinatario}</strong>
                    </div>
                  </div>
                ) : null}

                <div className="onboard-preview-note-footer">
                  <div>
                    <span>Cidade</span>
                    <strong>{nota.cidade}{nota.uf ? ` - ${nota.uf}` : ''}</strong>
                  </div>
                  <div>
                    <span>Praça</span>
                    <strong>{nota.praca}</strong>
                  </div>
                  <div>
                    <span>CD</span>
                    <strong>{nota.cd}</strong>
                  </div>
                  <div>
                    <span>Rota</span>
                    <strong>{nota.rota}</strong>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const renderHistoricoVazio = (label) => renderEmptyState(label, 'O preview usa apenas o payload já carregado na página.')

const renderAlteracoesTable = (rows) => {
  if (!rows?.length) {
    return renderHistoricoVazio('Nenhuma alteração registrada.')
  }

  return (
    <Table
      size="small"
      pagination={{ pageSize: 8 }}
      dataSource={rows.map((row, index) => ({ key: `alteracao-${index}`, ...row }))}
      className="onboard-preview-history-table"
      columns={[
        {
          title: 'Remetente',
          dataIndex: 'remetente',
          key: 'remetente'
        },
        {
          title: 'Nota',
          dataIndex: 'nota',
          key: 'nota'
        },
        {
          title: 'Ação',
          dataIndex: 'acao',
          key: 'acao',
          render: (value) => <Tag color="orange">{value || '-'}</Tag>
        },
        {
          title: 'Depois',
          dataIndex: 'depois',
          key: 'depois',
          render: (value) => <Tag color="blue">{value || '-'}</Tag>
        }
      ]}
    />
  )
}

const renderReimpressoesTable = (rows) => {
  if (!rows?.length) {
    return renderHistoricoVazio('Nenhuma reimpressão registrada.')
  }

  return (
    <Table
      size="small"
      pagination={{ pageSize: 8 }}
      dataSource={rows.map((row, index) => ({ key: `reimpressao-${index}`, ...row }))}
      className="onboard-preview-history-table"
      columns={[
        {
          title: 'Remetente',
          dataIndex: 'remetente',
          key: 'remetente'
        },
        {
          title: 'Nota',
          dataIndex: 'nota',
          key: 'nota'
        },
        {
          title: 'Volume',
          key: 'volume',
          render: (_, record) => <Tag color="purple">{record.volume || record.depois || record.antes || 'N/A'}</Tag>
        },
        {
          title: 'Data',
          dataIndex: 'data',
          key: 'data',
          render: (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-')
        }
      ]}
    />
  )
}

const renderMenu = (previewData) => {
  const produtividadeRows = Array.isArray(previewData?.produtividade) ? previewData.produtividade : []

  return (
    <div className="onboard-preview-menu">
      <div className="onboard-preview-menu-actions">
        <Button type="text" icon={<ReloadOutlined />} disabled />
        <Button type="text" icon={<MoreOutlined />} disabled />
      </div>

      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Card size="small" className="onboard-preview-productivity-card">
            <Statistic title="Notas com impressão" value={previewData?.totals?.notasImpressas || 0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="onboard-preview-productivity-card">
            <Statistic title="Notas sem impressão" value={previewData?.totals?.notasNaoImpressas || 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="onboard-preview-productivity-card">
            <Statistic title="Etiquetas impressas" value={previewData?.totals?.etiquetasImpressas || 0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="onboard-preview-productivity-card">
            <Statistic title="Etiquetas não impressas" value={previewData?.totals?.etiquetasNaoImpressas || 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="onboard-preview-productivity-card">
            <Statistic title="Clientes" value={previewData?.coletas?.length || 0} />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="onboard-preview-productivity-card">
            <Statistic
              title="Primeira etiqueta"
              value={previewData?.totals?.primeiraEtiqueta ? dayjs(previewData.totals.primeiraEtiqueta).format('DD/MM/YYYY HH:mm') : '---'}
              valueStyle={{ fontSize: 14 }}
            />
          </Card>
        </Col>
      </Row>

      <div className="onboard-preview-productivity-table-wrap">
        <Table
          size="small"
          pagination={false}
          className="onboard-preview-history-table"
          dataSource={produtividadeRows.map((row) => ({
            ...row,
            primeiraEtiqueta: row.primeiraEtiqueta ? dayjs(row.primeiraEtiqueta).format('DD/MM/YYYY HH:mm') : '---',
            ultimaEtiqueta: row.ultimaEtiqueta ? dayjs(row.ultimaEtiqueta).format('DD/MM/YYYY HH:mm') : '---'
          }))}
          columns={[
            { title: 'Cliente', dataIndex: 'cliente', key: 'cliente' },
            { title: 'Primeira', dataIndex: 'primeiraEtiqueta', key: 'primeiraEtiqueta' },
            { title: 'Última', dataIndex: 'ultimaEtiqueta', key: 'ultimaEtiqueta' }
          ]}
        />
      </div>
    </div>
  )
}

const OnboardPreviewModal = ({ open, onClose, previewData, previewRecord, theme = 'light' }) => {
  const [activeTab, setActiveTab] = useState('coletas')
  const [selectedColeta, setSelectedColeta] = useState(null)
  const [expandedNotas, setExpandedNotas] = useState([])

  useEffect(() => {
    if (open) {
      setActiveTab('coletas')
      setExpandedNotas([])
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [open, onClose])

  const coletas = previewData?.coletas || []

  useEffect(() => {
    setSelectedColeta(previewData?.clienteSelecionadoInicial || coletas[0] || null)
  }, [previewData, coletas])

  const handleSelectColeta = (coleta) => {
    setSelectedColeta(coleta)
    setExpandedNotas([])
    setActiveTab('notas')
  }

  const handleToggleNota = (notaKey) => {
    setExpandedNotas((current) => (
      current.includes(notaKey)
        ? current.filter((item) => item !== notaKey)
        : [...current, notaKey]
    ))
  }

  const pesoPercent = previewData?.vehicleData?.pesoVeiculo && previewData.vehicleData.pesoVeiculo > 0
    ? (Number(previewData?.totals?.peso || 0) / Number(previewData.vehicleData.pesoVeiculo) * 100)
    : previewData?.vehicleData?.capacidadeIdeal
      ? (Number(previewData?.totals?.peso || 0) / Number(previewData.vehicleData.capacidadeIdeal) * 100)
      : 0

  const m3Percent = previewData?.vehicleData?.m3Veiculo && previewData.vehicleData.m3Veiculo > 0
    ? (Number(previewData?.totals?.m3 || 0) / Number(previewData.vehicleData.m3Veiculo) * 100)
    : previewData?.vehicleData?.m3Calculado
      ? (Number(previewData?.totals?.m3 || 0) / Number(previewData.vehicleData.m3Calculado) * 100)
      : 0

  const tabItems = useMemo(() => ([
    {
      key: 'coletas',
      label: 'Coletas',
      children: renderColetas(coletas, handleSelectColeta, selectedColeta)
    },
    {
      key: 'notas',
      label: 'Notas',
      children: renderNotas(selectedColeta, expandedNotas, handleToggleNota)
    },
    {
      key: 'alteracoes',
      label: 'Alterações',
      children: renderAlteracoesTable(previewData?.alteracoes || [])
    },
    {
      key: 'reimpressoes',
      label: 'Reimpressões',
      children: renderReimpressoesTable(previewData?.reimpressoes || [])
    },
    {
      key: 'menu',
      label: 'Menu',
      children: renderMenu(previewData)
    }
  ]), [coletas, expandedNotas, previewData, selectedColeta])

  if (!open) return null

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.()
    }
  }

  return (
    <div className="onboard-preview-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className={`onboard-preview-shell ${theme === 'dark' ? 'is-dark' : 'is-light'}`} onClick={handleOverlayClick}>
        <div className="onboard-preview-phone" onClick={(event) => event.stopPropagation()}>
          <div className="onboard-preview-notch" />
          <div className="onboard-preview-screen">
            <div className="onboard-preview-navbar">
              <div className="onboard-preview-navbar-branch">
                <div className="onboard-preview-nav-title">{previewData?.meta?.empresa || previewRecord?.empresa || '-'}</div>
                <div className="onboard-preview-nav-subtitle">FILIAL</div>
              </div>
              <div className="onboard-preview-navbar-employee">
                <div className="onboard-preview-nav-title">{previewData?.meta?.veiculo || previewRecord?.veiculo || '-'}</div>
                <div className="onboard-preview-nav-subtitle">{previewData?.meta?.romaneio || previewRecord?.romaneio || '-'}</div>
              </div>
              <div className="onboard-preview-navbar-controls">
                <MenuOutlined className="onboard-preview-navbar-icon" />
                <UserOutlined
                  className="onboard-preview-navbar-icon"
                  style={{ marginLeft: 4, padding: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 4 }}
                />
              </div>
            </div>

            <div className="onboard-preview-content">
              <div className="onboard-preview-top-card onboard-preview-totals-card">
                <div className="onboard-preview-title-row">
                  <span>
                    {previewData?.meta?.origemVinculo === 'USUARIO' ? <UserOutlined /> : <TruckOutlined />}
                    {' '}
                    {previewData?.meta?.origemVinculo === 'USUARIO' ? 'Totais do usuário' : 'Totais do veículo'}
                  </span>
                  <Tag color="blue">Preview</Tag>
                </div>

                <Row gutter={[8, 8]}>
                  <Col flex="1">
                    <Statistic title="Notas" value={previewData?.totals?.notas || 0} />
                  </Col>
                  <Col flex="1">
                    <Statistic title="Vols" value={previewData?.totals?.vols || 0} />
                  </Col>
                  <Col flex="1">
                    <Statistic title="Peso" value={previewData?.totals?.peso || 0} precision={2} />
                  </Col>
                  <Col flex="1">
                    <Statistic title="M3" value={previewData?.totals?.m3 || 0} precision={2} />
                  </Col>
                  <Col flex="1">
                    <Statistic title="Falta" value={previewData?.totals?.falta || 0} />
                  </Col>
                </Row>

                <div className="onboard-preview-progress-section">
                  <Row gutter={[8, 8]} align="middle">
                    <Col flex="none" className="onboard-preview-progress-label">Peso</Col>
                    <Col flex="auto">
                      <Progress percent={Number.isFinite(pesoPercent) ? pesoPercent : 0} showInfo={false} size="small" strokeColor={getProgressColor(pesoPercent)} />
                    </Col>
                    <Col flex="none" className="onboard-preview-progress-percentage">{(Number.isFinite(pesoPercent) ? pesoPercent : 0).toFixed(2)}%</Col>
                  </Row>
                  <Row gutter={[8, 8]} align="middle">
                    <Col flex="none" className="onboard-preview-progress-label">M3</Col>
                    <Col flex="auto">
                      <Progress percent={Number.isFinite(m3Percent) ? m3Percent : 0} showInfo={false} size="small" strokeColor={getProgressColor(m3Percent)} />
                    </Col>
                    <Col flex="none" className="onboard-preview-progress-percentage">{(Number.isFinite(m3Percent) ? m3Percent : 0).toFixed(2)}%</Col>
                  </Row>
                </div>
              </div>

              <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="small" className="onboard-preview-tabs" />
            </div>

            <div className="onboard-preview-footer-appbar">
              <div className="onboard-preview-footer-toolbar">
                <div className="onboard-preview-footer-item text-left active">
                  <span className="onboard-preview-printer-name">Preview</span>
                </div>

                <div className="onboard-preview-footer-book text-center">
                  <div className="onboard-preview-footer-fab-container" />
                  <button type="button" disabled className="onboard-preview-fab">
                    <ScanOutlined />
                  </button>
                </div>

                <div className="onboard-preview-printer-container">
                  <div className="onboard-preview-printer-small active">
                    <PrinterOutlined />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardPreviewModal
